import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppWindow, BarChart3, ChevronLeft, ChevronRight, Loader2, MessageSquare } from 'lucide-react';
import { UsageAnalyticsPageHeader } from '../../components/UsageAnalyticsPageHeader';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { useEmbedSessions, type EmbedFlow, type EmbedSessionSummary } from '../../services/embedSessions.store';

const PAGE_SIZE = 25;

const FLOW_ICONS: Record<EmbedFlow, typeof AppWindow> = {
  dashboard: BarChart3,
  chart: BarChart3,
  chat: MessageSquare,
};

function fmtDateTime(value: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

/** What was embedded: the target's name when we could resolve it, else its id. */
function embeddedLabel(session: EmbedSessionSummary): string {
  if (session.target_label) return session.target_label;
  if (session.target_id) return session.target_id;
  return '—';
}

export default function EmbedSessions() {
  const [page, setPage] = useState(1);
  const [redeemedOnly, setRedeemedOnly] = useState(false);
  const { data, isLoading, isError } = useEmbedSessions({ page, pageSize: PAGE_SIZE, redeemedOnly });

  const sessions = data?.records ?? [];
  const total = data?.total_records ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      <UsageAnalyticsPageHeader tab="embed-sessions" />

      <div className="flex flex-wrap items-center justify-between gap-3 w-full px-4 md:px-6 py-3 border-b border-border bg-background flex-shrink-0">
        <div className="flex items-center gap-2">
          <Switch
            id="redeemed-only"
            checked={redeemedOnly}
            onCheckedChange={(checked) => { setRedeemedOnly(checked); setPage(1); }}
          />
          <Label htmlFor="redeemed-only" className="text-sm cursor-pointer">
            Opened sessions only
          </Label>
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">
          {total === 0 ? 'No sessions' : `${start}–${end} of ${total}`}
        </span>
      </div>

      <div className="flex-1 overflow-auto p-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading embed sessions…</p>
            </div>
          </div>
        ) : isError ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">Failed to load embed sessions. Please try again.</p>
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 h-full text-center text-muted-foreground">
            <AppWindow className="h-10 w-10" />
            <p className="text-lg font-medium text-foreground">No embed sessions yet</p>
            <p className="max-w-md text-sm">
              Sessions appear here once something calls <code className="font-mono text-xs">POST /api/v1/ext/init-session</code>.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Created</TableHead>
                    <TableHead>Embedded</TableHead>
                    <TableHead>Sub</TableHead>
                    <TableHead>Branch</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((session) => {
                    const FlowIcon = FLOW_ICONS[session.flow] ?? AppWindow;
                    return (
                      <TableRow key={session.jti}>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {fmtDateTime(session.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <FlowIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex flex-col min-w-0">
                              <span className="truncate font-medium">{embeddedLabel(session)}</span>
                              <span className="text-xs text-muted-foreground capitalize">{session.flow}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col min-w-0">
                            <span className="font-mono text-xs truncate">{session.sub_id}</span>
                            {session.email ? (
                              <span className="text-xs text-muted-foreground truncate">{session.email}</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{session.branch}</Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {session.redeemed_at ? (
                            fmtDateTime(session.redeemed_at)
                          ) : (
                            <span className="text-xs">Never opened</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/usage-analytics/embed-sessions/${session.jti}`}>Details</Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {totalPages > 1 && (
              <div className="mt-6 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
